import React, { useCallback, useEffect, useState } from 'react'
import { t, Trans } from '@lingui/macro'
import { Box, ChipsInput, HStack, Select } from 'front-end/src/components'
import { useUserAttributeSuggestionsLazyQuery } from 'front-end/src/pages/manage-campaign-page/components/campaign-audience/modals/EditCampaignAttributeFilter.codegen'
import { ReactSelectOption } from 'front-end/src/components/common/select/Select'
import { UserFilterComparatorInput } from 'front-end/src/graphql/private/PrivateSchemaTypes'
import { ATTRIBUTE_COMPARATOR_MAP } from 'shared/userFilterConstants'
import { isEqual } from 'lodash'
import { CampaignSearchableUserAttribute } from 'shared/enums/campaigns'
import { brandColors } from 'shared/basicPalette'
import { fontSizes } from 'front-end/src/components/chakraThemeExtension'

type EditCampaignAttributeFilterProps = {
  setCampaignFilterComparator: (comparator: UserFilterComparatorInput) => void
  campaignFilterComparator?: UserFilterComparatorInput | null
}

const MIN_SEARCH_LENGTH = 2

const selectOptionsToValueMap = (array: ReactSelectOption[]) => {
  return array.reduce((valueMap, item) => {
    valueMap[item.value] = item

    return valueMap
  }, {} as { [key: string]: ReactSelectOption })
}

export const EditCampaignAttributeFilter = ({
  setCampaignFilterComparator,
  campaignFilterComparator
}: EditCampaignAttributeFilterProps) => {
  // Needed to be part of component body to be translatable after language change
  const ATTRIBUTE_OPTIONS = [
    { label: t`Email`, value: CampaignSearchableUserAttribute.email },
    {
      label: t`Account name`,
      value: CampaignSearchableUserAttribute.account_name
    },
    {
      label: t`Display name`,
      value: CampaignSearchableUserAttribute.display_name
    },
    { label: t`First name`, value: CampaignSearchableUserAttribute.first_name },
    { label: t`Last name`, value: CampaignSearchableUserAttribute.last_name },
    { label: t`Position`, value: CampaignSearchableUserAttribute.position },
    { label: t`Timezone`, value: CampaignSearchableUserAttribute.timezone },
    {
      label: t`Employee id`,
      value: CampaignSearchableUserAttribute.employee_id
    },
    { label: t`Area`, value: CampaignSearchableUserAttribute.area },
    { label: t`Location`, value: CampaignSearchableUserAttribute.location },
    { label: t`Department`, value: CampaignSearchableUserAttribute.department },
    { label: t`Custom 1`, value: CampaignSearchableUserAttribute.custom_1 },
    { label: t`Custom 2`, value: CampaignSearchableUserAttribute.custom_2 },
    { label: t`Custom 3`, value: CampaignSearchableUserAttribute.custom_3 },
    {
      label: t`Phone number`,
      value: CampaignSearchableUserAttribute.phone_number
    },
    {
      label: t`Manager email`,
      value: CampaignSearchableUserAttribute.manager_email
    },
    { label: t`Language`, value: CampaignSearchableUserAttribute.locale }
  ]

  const COMPARATOR_OPTIONS = [
    { label: t`is`, value: ATTRIBUTE_COMPARATOR_MAP.in },
    { label: t`is not`, value: ATTRIBUTE_COMPARATOR_MAP['not in'] },
    { label: t`contains`, value: ATTRIBUTE_COMPARATOR_MAP.contains },
    { label: t`is empty`, value: ATTRIBUTE_COMPARATOR_MAP.is_empty }
  ]

  const COMPARATOR_OPTIONS_MAP = selectOptionsToValueMap(COMPARATOR_OPTIONS)
  const ATTRIBUTE_OPTIONS_MAP = selectOptionsToValueMap(ATTRIBUTE_OPTIONS)

  const [attribute, setAttribute] = useState<ReactSelectOption | undefined>(
    (campaignFilterComparator &&
      campaignFilterComparator?.field &&
      ATTRIBUTE_OPTIONS_MAP[campaignFilterComparator?.field]) ||
      undefined
  )
  const [comparator, setComparator] = useState<ReactSelectOption>(
    (campaignFilterComparator &&
      campaignFilterComparator?.operator &&
      COMPARATOR_OPTIONS_MAP[campaignFilterComparator.operator]) ||
      COMPARATOR_OPTIONS[0]
  )
  const [filterValue, setFilter] = useState<string[]>(
    (campaignFilterComparator && campaignFilterComparator?.value) || []
  )

  const [suggestions, setSuggestions] = useState<string[]>([])
  const [getSuggestions] = useUserAttributeSuggestionsLazyQuery({
    onCompleted: (data) => {
      const newSuggestions =
        data?.admin?.organisation?.userAttributeSuggestions || []

      setSuggestions(newSuggestions)
    }
  })
  const isContainsComparator =
    comparator.value === ATTRIBUTE_COMPARATOR_MAP.contains
  const isEmptyComparator =
    comparator.value === ATTRIBUTE_COMPARATOR_MAP.is_empty
  const isAutocompleteDisabled = !attribute?.value
  const placeholder =
    filterValue.length > 0
      ? ''
      : t`Start typing for a value, or paste comma separated values`

  const isFilterValueUpdated = !isEqual(
    filterValue,
    campaignFilterComparator?.value
  )
  const isComparatorUpdated =
    comparator.value !== campaignFilterComparator?.operator
  const isAttributeUpdated =
    attribute?.value !== campaignFilterComparator?.field

  const isFilterUpdated =
    isFilterValueUpdated || isComparatorUpdated || isAttributeUpdated

  const resetSuggestionsAndFilter = useCallback(() => {
    setSuggestions([])
    setFilter([])
  }, [])

  const handleFetchOptions = (query: string) => {
    const isIncorrectQuery = !query || query.length < MIN_SEARCH_LENGTH

    if (isIncorrectQuery || !attribute?.value || isContainsComparator) {
      if (isIncorrectQuery) setSuggestions([])

      return
    }

    getSuggestions({ variables: { attribute: attribute.value, search: query } })
  }

  useEffect(() => {
    if (isFilterUpdated) {
      setCampaignFilterComparator({
        value: filterValue,
        operator: comparator.value,
        field: attribute?.value
      })
    }
  }, [filterValue, comparator.value, attribute?.value])

  return (
    <Box flexGrow={1}>
      <HStack spacing="16px">
        <Select
          options={ATTRIBUTE_OPTIONS}
          placeholder={t`Select attribute`}
          minWidth={160}
          onChange={(value) => {
            value && setAttribute(value)
            resetSuggestionsAndFilter()
          }}
          defaultValue={attribute}
        />
        <Select
          options={COMPARATOR_OPTIONS}
          defaultValue={comparator}
          onChange={(value) => {
            value && setComparator(value)
            resetSuggestionsAndFilter()
          }}
        />
      </HStack>
      {!isEmptyComparator && (
        <ChipsInput
          value={filterValue}
          helpOptions={suggestions}
          handleChange={setFilter}
          mt="8px"
          allowFreeText
          fetchOptions={isContainsComparator ? undefined : handleFetchOptions}
          isDisabled={isAutocompleteDisabled}
          placeholder={placeholder}
        />
      )}
      {campaignFilterComparator?.field ===
        CampaignSearchableUserAttribute.locale && (
        <Box mt="16px" fontSize={fontSizes.sm} color={brandColors.grey.mid}>
          <Trans>
            You have a choice of several languages. Please use following
            abbreviation: enUs = English (US), enGb = English (UK), ptBr =
            Português (BR), ptPt= Português (PT), esEs = Español, deDe =
            Deutsch, itIt= Italiano, frFr = Français, zhHans = 簡體中文, zhHant
            = 中國傳統的, viVn = Vietnamese
          </Trans>
        </Box>
      )}
    </Box>
  )
}
